// ── 5 Integrationen & Skills ── Zustand, Rumpf und Gruppen (rein).

import type { Catalog, IntegrationDef, Tenant } from "../../api/types";

/** Die Gruppen der Integrationen im Formular — Schlüssel aus `src/integrations.ts`. */
export const INTEGRATION_GROUPS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ["Microsoft", ["m365", "gdap", "sharepoint", "dynamics365"]],
  ["Entwicklung und Wissen", ["gitlab", "sentry", "linear", "docmost", "isms", "drawio"]],
  ["IT-Betrieb und Distribution", ["infinigate", "itglue", "datto-rmm", "starface", "unifi", "eset"]],
  ["Sprache, Bilder, Suche", ["whisper", "tts", "voice-call", "images", "web-search", "prompt-guard"]],
  ["Weitere", []],
  ["Solutec-intern", []],
];

/**
 * Nach Herkunft gruppiert statt einer langen Liste; Solutec-intern zuletzt,
 * Unbekanntes unter „Weitere", leere Gruppen entfallen. Innerhalb einer Gruppe
 * die Reihenfolge des Katalogs.
 */
export function groupIntegrations(defs: readonly IntegrationDef[]): Array<[string, IntegrationDef[]]> {
  const groups = new Map<string, IntegrationDef[]>(INTEGRATION_GROUPS.map(([title]) => [title, []]));
  for (const def of defs) {
    const title = def.internal ? "Solutec-intern" : (INTEGRATION_GROUPS.find(([, keys]) => keys.includes(def.key))?.[0] ?? "Weitere");
    const list = groups.get(title) ?? [];
    list.push(def);
    groups.set(title, list);
  }
  return [...groups].filter(([, list]) => list.length > 0);
}

export interface IntegrationsState {
  enabled: Record<string, boolean>;
  /** Integration → Variable → eingetippter Wert. Geheimnisse beginnen leer. */
  values: Record<string, Record<string, string>>;
  skills: string[];
}

export interface IntegrationsBody {
  integrations: Record<string, boolean>;
  integration_values: Record<string, Record<string, string>>;
  skills: string[];
}

export function integrationsFromTenant(t: Tenant, cat: Catalog): IntegrationsState {
  const enabled: Record<string, boolean> = {};
  const values: Record<string, Record<string, string>> = {};
  for (const def of cat.integrations) {
    enabled[def.key] = Boolean(t.integrations[def.key]);
    const stored = t.integration_values[def.key] ?? {};
    values[def.key] = Object.fromEntries(def.fields.map((f) => [f.var, f.secret ? "" : stored[f.var] ?? ""]));
  }
  return { enabled, values, skills: [...t.skills] };
}

export function integrationsToBody(s: IntegrationsState, cat: Catalog): IntegrationsBody {
  const integrations: Record<string, boolean> = {};
  const values: Record<string, Record<string, string>> = {};
  for (const def of cat.integrations) {
    integrations[def.key] = s.enabled[def.key] ?? false;
    const out: Record<string, string> = {};
    for (const f of def.fields) {
      const v = s.values[def.key]?.[f.var] ?? "";
      // Ein leeres Geheimnis heißt „behalten" und geht nicht mit; alles andere,
      // wie es dasteht (auch leer — so löscht man einen Wert).
      if (f.secret) { if (v.trim()) out[f.var] = v.trim(); } else out[f.var] = v;
    }
    values[def.key] = out;
  }
  return {
    integrations,
    integration_values: values,
    // Nur Skills, die der Katalog führt, in seiner Reihenfolge.
    skills: cat.standalone_skills.map((sk) => sk.dir).filter((dir) => s.skills.includes(dir)),
  };
}
